import { useMemo } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
  Table,
  ColumnTable,
  Icon,
  Button,
  IconNameType,
} from '@octano/global-ui';
import { TranslationsKeys } from '../../../locales/translations';
import { IClassesBookClassItem, LessonActivity } from '../types';
import TableEmptyContent from '../../../components/Text/TableEmptyContent';

type ClassesBookClassActivitiesProps = {
  row: IClassesBookClassItem;
  activitiesLimit?: number;
  onPressAdd: (classItem: IClassesBookClassItem) => void;
  onPressEdit: (
    classItem: IClassesBookClassItem,
    activity: LessonActivity,
  ) => void;
  onPressRemove: (
    classItem: IClassesBookClassItem,
    activity: LessonActivity,
  ) => void;
};

const ClassesBookClassActivities = ({
  row,
  activitiesLimit,
  onPressAdd,
  onPressEdit,
  onPressRemove,
}: ClassesBookClassActivitiesProps) => {
  const { t } = useTranslation(TranslationsKeys.CLASSES_BOOK_EDIT);

  const columns = useMemo<ColumnTable<LessonActivity>[]>(() => {
    return [
      {
        headerText: t('activities.table.unit'),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        columnName: 'courseLearningUnit',
        cellFormat({ value }) {
          return value?.title;
        },
      },
      {
        headerText: t('activities.table.criteria'),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        columnName: 'criteria',
      },
      {
        headerText: t('activities.table.contents'),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        columnName: 'contents',
      },
      {
        headerText: t('activities.table.activities'),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        columnName: 'activities',
      },
      {
        headerText: t('activities.table.action'),
        tdClassName: 'text-right',
        thClassName: 'text-right',
        columnName: 'id',
        cellFormat({ row: item }) {
          return (
            <div
              className="d-flex w-100 align-items-center"
              style={{ gap: 10 }}
            >
              <div className="flex-fill"></div>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => onPressEdit(row, item)}
              >
                <Icon name="edit" />
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => onPressRemove(row, item)}
              >
                <Icon name="trash" />
              </button>
            </div>
          );
        },
      },
    ];
  }, [t, row, onPressEdit, onPressRemove]);

  const noResultsText = useMemo(() => {
    const icon: IconNameType = 'information';
    const title = t('activities.empty.title');
    const subtitle = t('activities.empty.description');

    return <TableEmptyContent icon={icon} title={title} subtitle={subtitle} />;
  }, [t]);

  return (
    <Container fluid>
      <Row className="align-items-center justify-content-center">
        <Col xs={12}>
          <Table
            borderless={false}
            color="transparent"
            data={row?.activities || []}
            noResultsText={noResultsText}
            columns={columns}
            notResponsive
          />
        </Col>
        {!!(
          !activitiesLimit || (row?.activities.length || 0) < activitiesLimit
        ) && (
          <Col xs={12} className="mt-3">
            <div className="d-flex w-100 justify-content-end">
              <Button
                text={t(`activities.add`)}
                icon="plus"
                size="md"
                onClick={() => onPressAdd(row)}
              />
            </div>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default ClassesBookClassActivities;
